import React, { useMemo } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Download, Filter, TrendingUp, Clock, Briefcase } from 'lucide-react';
import type { Timesheet, Employee, Project } from '@/types';
import type { UtilisationMetrics } from '@/types/utilisation';
import { calculateEmployeeDetailData, exportEmployeeReportCSV } from '@/utils/employeeDetailUtils';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface EmployeeDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: UtilisationMetrics;
  timesheets: Timesheet[];
  employees: Employee[];
  projects: Project[];
  dateRange: { start: Date; end: Date };
  onFilterToEmployee?: (employeeId: number) => void;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export const EmployeeDetailModal: React.FC<EmployeeDetailModalProps> = ({
  isOpen,
  onClose,
  employee,
  timesheets,
  employees,
  projects,
  dateRange,
  onFilterToEmployee,
}) => {
  // Calculate detailed data
  const detailData = useMemo(
    () => calculateEmployeeDetailData(employee, timesheets, employees, projects, dateRange),
    [employee, timesheets, employees, projects, dateRange]
  );

  // Handle export
  const handleExport = () => {
    try {
      const csv = exportEmployeeReportCSV(detailData);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee-${employee.employeeName.replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Employee report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // Handle filter to employee
  const handleFilterToEmployee = () => {
    if (onFilterToEmployee) {
      onFilterToEmployee(employee.employeeId);
      onClose();
    }
  };

  // Prepare chart data
  const weeklyChartData = detailData.weeklyBreakdown.map((week) => ({
    week: week.week,
    'Total Hours': week.totalHours,
    'Billable Hours': week.billableHours,
  }));

  const projectPieData = detailData.projectAllocation.slice(0, 6).map((project, index) => ({
    name: project.projectName,
    value: project.hours,
    percentage: project.percentage,
    fill: COLORS[index % COLORS.length],
  }));

  const dailyActivityData = detailData.dailyActivity.map((day) => ({
    day: day.day,
    hours: day.hours,
    billable: day.billableHours,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {employee.employeeName}
          </DialogTitle>
          <DialogDescription>
            {employee.department} • {format(dateRange.start, 'MMM d')} -{' '}
            {format(dateRange.end, 'MMM d, yyyy')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="max-h-[calc(90vh-180px)] pr-4">
            <div className="space-y-6 pb-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{employee.totalHours.toFixed(1)}</div>
                  <p className="text-xs text-muted-foreground">Total Hours</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-success-600 dark:text-success-400">
                    {employee.billableHours.toFixed(1)}
                  </div>
                  <p className="text-xs text-muted-foreground">Billable Hours</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {(employee.utilisationRate * 100).toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">Utilisation Rate</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{employee.projectCount}</div>
                  <p className="text-xs text-muted-foreground">Projects</p>
                </CardContent>
              </Card>
            </div>

            {/* Weekly Breakdown Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Weekly Hours Breakdown</CardTitle>
                <CardDescription>Total and billable hours by week</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={weeklyChartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="week" className="text-xs" tick={{ fill: 'currentColor' }} />
                    <YAxis className="text-xs" tick={{ fill: 'currentColor' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Total Hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Billable Hours" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Project Allocation */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Project Allocation</CardTitle>
                  <CardDescription>Time distribution across projects</CardDescription>
                </CardHeader>
                <CardContent>
                  {projectPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={projectPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(props) => `${(Number(props.percent ?? 0) * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {projectPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--background))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '6px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                      No project data available
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Project Details</CardTitle>
                  <CardDescription>Hours per project</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {detailData.projectAllocation.map((project, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <span className="truncate max-w-[150px]">{project.projectName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{project.hours.toFixed(1)}h</span>
                            <Badge variant="secondary" className="text-xs">
                              {project.percentage.toFixed(0)}%
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Daily Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily Activity</CardTitle>
                <CardDescription>Hours logged per day</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={dailyActivityData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="day" className="text-xs" tick={{ fill: 'currentColor' }} />
                    <YAxis className="text-xs" tick={{ fill: 'currentColor' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="hours"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="Total Hours"
                    />
                    <Line
                      type="monotone"
                      dataKey="billable"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="Billable Hours"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Additional Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Additional Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Peak Day</p>
                    <p className="font-semibold capitalize">{employee.peakDay}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Peak Hours</p>
                    <p className="font-semibold">{employee.peakHours.toFixed(1)}h</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Target Hours</p>
                    <p className="font-semibold">{employee.targetHours.toFixed(1)}h</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Variance</p>
                    <p
                      className={cn(
                        'font-semibold',
                        employee.variance >= 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                      )}
                    >
                      {employee.variance >= 0 ? '+' : ''}
                      {employee.variance.toFixed(1)}h
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Non-Billable Hours</p>
                    <p className="font-semibold">{employee.nonBillableHours.toFixed(1)}h</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Billable %</p>
                    <p className="font-semibold">
                      {((employee.billableHours / employee.totalHours) * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          {onFilterToEmployee && (
            <Button variant="outline" onClick={handleFilterToEmployee} className="gap-2">
              <Filter className="h-4 w-4" />
              Filter to This Employee
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={handleExport} className="gap-2">
              <Download className="h-4 w-4" />
              Export Report
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
